import { useState, type MouseEvent } from 'react'
import { Box, IconButton, Popover, Tooltip, Typography } from '@mui/material'
import TuneIcon from '@mui/icons-material/Tune'
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank'
import CheckBoxIcon from '@mui/icons-material/CheckBox'

import { colorTokens } from '@/apps/latest/shared/styles/colorTokens'
import { RADIUS } from '@/shared/styles/radii'

// 録音前メタ設定 (= 「同伴/代理人有」) を露出する popover.
// trigger は ⫻ (Tune) icon button. Notion 28 spec § 4.5 (popover) + § 4.3 secondary ボタン形.
//
// patients が 1 件: flat な checkbox 1 行 (= 単独録音).
// patients が 2+: 患者ごとに nested checkbox 行 (= まとめて録音).
//
// 将来 Instructions / Consent 等の項目を追加する場合は items 配列で渡す形に拡張可能.

export interface CompanionPatient {
  id: string
  name: string
  hasCompanion: boolean
}

interface Props {
  patients: CompanionPatient[]
  onToggle: (patientId: string, value: boolean) => void
  disabled?: boolean
}

export const RecordingSettingsPopover = ({
  patients,
  onToggle,
  disabled = false,
}: Props) => {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null)
  const open = Boolean(anchor)
  const isSingle = patients.length === 1

  const handleOpen = (e: MouseEvent<HTMLButtonElement>) => {
    setAnchor(e.currentTarget)
  }
  const handleClose = () => {
    setAnchor(null)
  }

  return (
    <>
      <Tooltip title="録音設定" placement="bottom">
        <span>
          <IconButton
            size="small"
            onClick={handleOpen}
            disabled={disabled}
            aria-label="録音設定"
            sx={{
              width: 32,
              height: 32,
              color: colorTokens.fg.muted,
              '&:hover': { bgcolor: colorTokens.overlay.hover },
            }}
          >
            <TuneIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </span>
      </Tooltip>
      <Popover
        open={open}
        anchorEl={anchor}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: {
            elevation: 0,
            sx: {
              mt: 0.5,
              minWidth: 200,
              borderRadius: `${RADIUS.lg}px`,
              p: 0.5,
              border: `1px solid ${colorTokens.border.subtle}`,
              boxShadow: colorTokens.shadow.popover,
            },
          },
        }}
      >
        {isSingle ? (
          <CompanionRow
            label="同伴/代理人有"
            checked={patients[0].hasCompanion}
            onToggle={(v) => onToggle(patients[0].id, v)}
          />
        ) : (
          <Box>
            <Typography
              component="div"
              sx={{
                px: 1,
                py: 0.75,
                fontSize: 12,
                fontWeight: 500,
                color: colorTokens.fg.muted,
              }}
            >
              同伴/代理人有
            </Typography>
            {patients.map((p) => (
              <CompanionRow
                key={p.id}
                label={p.name}
                checked={p.hasCompanion}
                onToggle={(v) => onToggle(p.id, v)}
                indent
              />
            ))}
          </Box>
        )}
      </Popover>
    </>
  )
}

interface RowProps {
  label: string
  checked: boolean
  onToggle: (v: boolean) => void
  indent?: boolean
}

const CompanionRow = ({
  label,
  checked,
  onToggle,
  indent = false,
}: RowProps) => (
  <Box
    role="button"
    tabIndex={0}
    onClick={() => onToggle(!checked)}
    onKeyDown={(e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault()
        onToggle(!checked)
      }
    }}
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 1,
      height: 32,
      px: indent ? 2 : 1,
      borderRadius: `${RADIUS.md}px`,
      cursor: 'pointer',
      fontSize: 14,
      color: colorTokens.fg.default,
      '&:hover': { bgcolor: colorTokens.overlay.hover },
      '&:focus-visible': {
        outline: 'none',
        bgcolor: colorTokens.overlay.hover,
      },
    }}
  >
    {checked ? (
      <CheckBoxIcon sx={{ fontSize: 18, color: colorTokens.accent.primary }} />
    ) : (
      <CheckBoxOutlineBlankIcon
        sx={{ fontSize: 18, color: colorTokens.fg.faint }}
      />
    )}
    <Typography
      component="span"
      sx={{ fontSize: 14, color: colorTokens.fg.default }}
    >
      {label}
    </Typography>
  </Box>
)
